<template>
  <div class="heading-dropdown" ref="dropdown">
    <button 
      class="heading-bbl-dropdown-trigger"
      @click="toggleDropdown"
      :class="{ 'is-open': isOpen }"
    >
      <span class="heading-preview" :class="`preview-${currentValue}`">
        {{ currentLabel }}
      </span>
      <svg class="dropdown-icon" :class="{ 'rotate': isOpen }" width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
    </button>
    
    <transition name="dropdown-fade">
      <div v-if="isOpen" class="heading-bbl-dropdown-menu">
        <div class="bbl-dropdown-menu-inner">
          <button
            v-for="option in options"
            :key="option.value"
            class="heading-option"
            :class="{ 'is-active': currentValue === option.value }"
            @click="selectOption(option)"
          >
            <span class="option-preview" :class="`preview-${option.value}`">
              {{ option.label }}
            </span>
            <svg v-if="currentValue === option.value" class="check-icon" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M13.5 4L6 11.5L2.5 8" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'HeadingDropdown',
  props: {
    modelValue: {
      type: String,
      default: ''
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      isOpen: false,
      options: [
        { value: 'paragraph', label: 'Paragraph' },
        { value: '1', label: 'Heading 1' },
        { value: '2', label: 'Heading 2' },
        { value: '3', label: 'Heading 3' },
        { value: '4', label: 'Heading 4' },
        { value: '5', label: 'Heading 5' },
        { value: '6', label: 'Heading 6' }
      ]
    }
  },
  computed: {
    currentValue() {
      return this.modelValue || 'paragraph'
    },
    currentLabel() {
      const option = this.options.find(opt => opt.value === this.currentValue)
      return option ? option.label : 'Select Style'
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },
    selectOption(option) {
      this.$emit('update:modelValue', option.value)
      this.$emit('change', { target: { value: option.value } })
      this.isOpen = false
    },
    handleClickOutside(event) {
      if (this.$refs.dropdown && !this.$refs.dropdown.contains(event.target)) {
        this.isOpen = false
      }
    }
  }
}
</script>

<style scoped>
.heading-dropdown {
  position: relative;
  display: inline-block;
}

.heading-bbl-dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1.5px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 12px;
  min-width: 140px;
  height: 32px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.heading-bbl-dropdown-trigger:hover {
  border-color: #3b82f6;
  box-shadow: 0 2px 4px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.heading-bbl-dropdown-trigger.is-open {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  background: white;
}

.heading-preview {
  flex: 1;
  text-align: left;
  font-weight: 500;
}

.dropdown-icon {
  color: #6b7280;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
}

.dropdown-icon.rotate {
  transform: rotate(180deg);
}

.heading-bbl-dropdown-menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  min-width: 200px;
  background: white;
  border: 1.5px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1), 0 4px 10px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  overflow: hidden;
}

.bbl-dropdown-menu-inner {
  padding: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.heading-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
  text-align: left;
  color: #374151;
  font-weight: 500;
}

.heading-option:hover {
  background: linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%);
}

.heading-option.is-active {
  background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
  color: #1e40af;
}

.option-preview {
  flex: 1;
}

/* Style previews for different heading levels */
.preview-paragraph {
  font-size: 13px;
  font-weight: 400;
}

.preview-1 {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.preview-2 {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.preview-3 {
  font-size: 15px;
  font-weight: 600;
}

.preview-4 {
  font-size: 14px;
  font-weight: 600;
}

.preview-5 {
  font-size: 13px;
  font-weight: 600;
}

.preview-6 {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.check-icon {
  color: #3b82f6;
  flex-shrink: 0;
  margin-left: 8px;
}

/* Dropdown animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-fade-enter-from {
  opacity: 0;
  transform: translateY(-8px) scale(0.95);
}

.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

/* Scrollbar styling */
.bbl-dropdown-menu-inner::-webkit-scrollbar {
  width: 6px;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-track {
  background: transparent;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 3px;
}

.bbl-dropdown-menu-inner::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
</style>
